import { useInjection } from 'inversify-react';
import { useCallback, useMemo } from 'react';

import { Confirm } from '@desygner/ui-common-components';
import { useTranslation } from '@desygner/ui-common-translation';
import { useWorkspaceStore } from '@desygner/ui-data-access-workspace';

import type { IWorkflowManager } from '../../../../../core/viewer/workflow-manager';
import { TYPES } from '../../../../../inversify.types';
import { ProjectAction } from '../../../../../store/viewer/viewer.types';

interface AdminApprovalContainerProps {
  action: ProjectAction | null;
  onClose?: () => void;
}

export function AdminApprovalContainer({ action, onClose }: AdminApprovalContainerProps) {
  const { t } = useTranslation();
  const workspaceStore = useWorkspaceStore();
  const workflowManager = useInjection<IWorkflowManager>(TYPES.Viewer.WorkflowManager);
  const isPending = useMemo<boolean>(
    () => workspaceStore.workflow.isActionPending(action ?? ''),
    [workspaceStore, action]
  );

  const onConfirm = useCallback(async () => {
    if (isPending) {
      onClose?.();
      return;
    }

    if (!action) return;

    onClose?.();
    workflowManager.createJob(action);
  }, [isPending, action, onClose, workflowManager]);
  const labels = useMemo(
    () => ({
      title: t('tool.topbar.workflow.action_approval.title', { action }),
      confirmButton: t(
        `tool.topbar.workflow.action_approval.confirm${isPending ? '.pending' : ''}`
      ),
      description: t(
        `tool.topbar.workflow.action_approval.description${isPending ? '.pending' : ''}`
      ),
    }),
    [action, isPending, t]
  );

  return action ? <Confirm labels={labels} onConfirm={onConfirm} onCancel={onClose} /> : null;
}
